import { useState } from 'react';
import { PlusCircle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { FixedAsset } from '@/hooks/useFixedAssets';
import { useLegalFeeStages } from '@/hooks/useLegalFeeStages';
import { computeTotalOutstanding } from './legalFees.utils';
import { StageCard } from './StageCard';
import { InitLegalFeesForm } from './InitLegalFeesForm';
import { formatCurrency } from '@/lib/currency.utils';

interface LegalFeesSectionProps {
  asset: FixedAsset;
}

export function LegalFeesSection({ asset }: LegalFeesSectionProps) {
  const [initOpen, setInitOpen] = useState(false);

  // Req 4.3: render nothing when not purchasing
  if (asset.ownership_status !== 'purchasing') {
    return null;
  }

  return <LegalFeesSectionInner asset={asset} initOpen={initOpen} setInitOpen={setInitOpen} />;
}

// Inner component so hooks are only called when ownership_status === 'purchasing'
function LegalFeesSectionInner({
  asset,
  initOpen,
  setInitOpen,
}: {
  asset: FixedAsset;
  initOpen: boolean;
  setInitOpen: (open: boolean) => void;
}) {
  const { data: stages, isLoading, isError } = useLegalFeeStages(asset.id);

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Legal Fees</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <Skeleton className="h-4 w-1/3" />
          <Skeleton className="h-4 w-1/2" />
          <Skeleton className="h-4 w-2/3" />
        </CardContent>
      </Card>
    );
  }

  if (isError) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Legal Fees</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-destructive">
            Failed to load legal fees. Please refresh and try again.
          </p>
        </CardContent>
      </Card>
    );
  }

  // Req 4.2, 9.1: no stages yet — show CTA
  if (!stages || stages.length === 0) {
    return (
      <>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle>Legal Fees</CardTitle>
            <Button size="sm" variant="outline" onClick={() => setInitOpen(true)}>
              <PlusCircle className="h-4 w-4 mr-2" />
              Add Legal Fee Tracking
            </Button>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground">
              No legal fee stages configured for this property. Add tracking to start recording
              purchase-related fees.
            </p>
          </CardContent>
        </Card>

        {/* InitLegalFeesForm */}
        <Dialog open={initOpen} onOpenChange={setInitOpen}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Add Legal Fee Tracking</DialogTitle>
            </DialogHeader>
            <InitLegalFeesForm
              fixedAssetId={asset.id}
              onSuccess={() => setInitOpen(false)}
            />
          </DialogContent>
        </Dialog>
      </>
    );
  }

  // Req 4.4: sorted by sort_order ascending (hook already returns sorted, but ensure in UI too)
  const sortedStages = [...stages].sort((a, b) => a.sort_order - b.sort_order);

  // Req 4.11: total outstanding across all items
  const allItems = sortedStages.flatMap((s) => s.items);
  const totalOutstanding = computeTotalOutstanding(allItems, asset.currency, {});

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle>Legal Fees</CardTitle>
            <span className="text-sm font-medium text-muted-foreground">
              Outstanding:{' '}
              <span className="text-foreground font-semibold">
                {formatCurrency(totalOutstanding, asset.currency)}
              </span>
            </span>
          </div>
        </CardHeader>
      </Card>

      {sortedStages.map((stage) => (
        <StageCard
          key={stage.id}
          stage={stage}
          fixedAssetId={asset.id}
          propertyName={asset.name}
          assetCurrency={asset.currency}
        />
      ))}
    </div>
  );
}
